/**
 * 摄像机球坐标 → 摄影术语映射工具
 * azimuth: 0-360° 方位角, elevation: -90~90° 俯仰角, distance: 0.5-3.0x 距离
 */

const AZIMUTH_SECTORS = [
  { min: 337.5, max: 360, label: '正面', en: 'Front view' },
  { min: 0, max: 22.5, label: '正面', en: 'Front view' },
  { min: 22.5, max: 67.5, label: '右前视角', en: 'Front-right 45° angle' },
  { min: 67.5, max: 112.5, label: '右侧', en: 'Right side view' },
  { min: 112.5, max: 157.5, label: '右后视角', en: 'Back-right angle' },
  { min: 157.5, max: 202.5, label: '背面', en: 'Rear view' },
  { min: 202.5, max: 247.5, label: '左后视角', en: 'Back-left angle' },
  { min: 247.5, max: 292.5, label: '左侧', en: 'Left side view' },
  { min: 292.5, max: 337.5, label: '左前视角', en: 'Front-left 45° angle' },
];

const ELEVATION_RANGES = [
  { min: -90, max: -50, label: '仰拍镜头', en: 'Low angle, looking up (worm\'s eye)' },
  { min: -50, max: -15, label: '微仰镜头', en: 'Slight low angle' },
  { min: -15, max: 15, label: '平视镜头', en: 'Eye-level shot' },
  { min: 15, max: 50, label: '俯拍镜头', en: 'High angle, looking down' },
  { min: 50, max: 90, label: '鸟瞰镜头', en: 'Bird\'s eye view, overhead shot' },
];

const DISTANCE_RANGES = [
  { min: 0.5, max: 1.0, label: '特写', en: 'Close-up shot' },
  { min: 1.0, max: 1.5, label: '中景', en: 'Medium shot' },
  { min: 1.5, max: 2.0, label: '全景', en: 'Full shot' },
  { min: 2.0, max: 3.0, label: '远景', en: 'Wide establishing shot' },
];

function findInRange(ranges, value) {
  for (const r of ranges) {
    if (value >= r.min && value < r.max) return r;
  }
  // fallback to last
  return ranges[ranges.length - 1];
}

function getAzimuthInfo(azimuth) {
  const a = ((azimuth % 360) + 360) % 360;
  // 特殊处理 337.5-360 范围
  if (a >= 337.5) return AZIMUTH_SECTORS[0];
  return findInRange(AZIMUTH_SECTORS.slice(1), a);
}

function getElevationInfo(elevation) {
  const e = Math.max(-90, Math.min(90, elevation));
  return findInRange(ELEVATION_RANGES, e);
}

function getDistanceInfo(distance) {
  const d = Math.max(0.5, Math.min(3.0, distance));
  return findInRange(DISTANCE_RANGES, d);
}

/**
 * 返回中文标签对象
 * @returns {{ direction: string, elevation: string, distance: string, fullText: string }}
 */
export function mapCameraToLabel(azimuth, elevation, distance) {
  const dir = getAzimuthInfo(azimuth);
  const elev = getElevationInfo(elevation);
  const dist = getDistanceInfo(distance);
  return {
    direction: dir.label,
    elevation: elev.label,
    distance: dist.label,
    fullText: `${dir.label} | ${elev.label} | ${dist.label}`,
  };
}

/**
 * 返回英文 prompt 片段，用于注入 AI 生图
 * @returns {string}
 */
export function mapCameraToPrompt(azimuth, elevation, distance) {
  const dir = getAzimuthInfo(azimuth);
  const elev = getElevationInfo(elevation);
  const dist = getDistanceInfo(distance);
  return (
    `CAMERA POSITION: ${dir.en}.` +
    ` CAMERA HEIGHT: ${elev.en}.` +
    ` SHOT DISTANCE: ${dist.en}.`
  );
}
